import React, { useState } from 'react';
import { useEditor } from '../../context/EditorContext';
import { useCart } from '../../context/CartContext';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from '../ui/sheet';
import { Button } from '../ui/button';
import { Loader2, ShoppingBag, Check } from 'lucide-react';
import api from '../../services/api';
import { logger } from '../../utils/logger';
import { OrderItemsList } from './OrderItemsList';
import { CustomerDetailsForm } from './CustomerDetailsForm';
import { useCheckoutItems } from './useCheckoutItems';
import { getDesignExports, validateCheckoutForm, buildOrderPayload } from './checkoutUtils';

const INITIAL_FORM = {
  fullName: '',
  email: '',
  phone: '',
  streetAddress: '',
  streetAddress2: '',
  postalCode: '',
  cityState: '',
  country: 'NO',
};

const ProcessingStep = () => (
  <div className="flex flex-col items-center justify-center py-20">
    <div className="relative">
      <Loader2 className="w-16 h-16 text-primary animate-spin" />
      <div className="absolute inset-0 w-16 h-16 bg-primary/20 rounded-full blur-xl animate-pulse" />
    </div>
    <p className="text-lg font-bold text-gray-800 mt-6">Processing your order...</p>
    <p className="text-sm text-gray-500 mt-2">Please don&apos;t close this window</p>
  </div>
);

const SuccessStep = ({ orderId }) => (
  <div className="flex flex-col items-center justify-center py-20">
    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center shadow-lg shadow-green-200">
      <Check className="w-10 h-10 text-green-600" />
    </div>
    <p className="text-xl font-bold text-gray-800 mt-6">Order Confirmed!</p>
    <p className="text-sm text-gray-500 mt-2 text-center">
      Check your email for order details.
    </p>
    {orderId && (
      <p className="text-xs text-gray-400 mt-4 font-mono">
        Order ID: {orderId.slice(0, 8)}...
      </p>
    )}
  </div>
);

export const CheckoutSheet = ({ open, onOpenChange, previewRef, quantity: initialQuantity = 1, preExportedDesigns = null }) => {
  const { getAllDesigns, resetEditor, loadDesigns } = useEditor();
  const { removeFromCart, updateCartItemQuantity, clearCart } = useCart();
  const {
    displayItems,
    orderTotals,
    totalPrice,
    currentDesigns,
    hasCurrentDesign,
    cartCount,
  } = useCheckoutItems(initialQuantity);

  const [step, setStep] = useState('form');
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [legalConfirmed, setLegalConfirmed] = useState(false);
  const [error, setError] = useState('');
  const [orderId, setOrderId] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleEditItem = (item) => {
    if (!item.isCurrent) {
      loadDesigns(item.designs, item.color, item.size);
      removeFromCart(item.id);
    }
    onOpenChange(false);
  };

  const handleCheckout = async () => {
    const validationError = validateCheckoutForm({
      formData, legalConfirmed, displayItems, hasCurrentDesign, cartCount,
    });
    if (validationError) {
      setError(validationError);
      return;
    }

    setStep('processing');
    setError('');

    try {
      const orderData = buildOrderPayload({
        formData,
        legalConfirmed,
        displayItems,
        designExports: getDesignExports(preExportedDesigns, previewRef),
        getAllDesigns,
        initialQuantity,
        totalPrice,
      });
      const order = await api.createOrder(orderData);
      setOrderId(order.id);

      const session = await api.createCheckoutSession(order.id, window.location.origin);
      window.location.href = session.url;
    } catch (err) {
      logger.error('Checkout failed:', err);
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
      setStep('form');
    }
  };

  const handleClose = () => {
    if (step === 'success') {
      resetEditor();
      clearCart();
    }
    setStep('form');
    setError('');
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={handleClose}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-lg overflow-y-auto"
        data-testid="checkout-sheet"
      >
        <SheetHeader>
          <SheetTitle className="font-heading text-2xl flex items-center gap-2">
            <ShoppingBag className="w-6 h-6 text-primary" />
            Checkout
          </SheetTitle>
          <SheetDescription>
            Complete your order for custom t-shirts
          </SheetDescription>
        </SheetHeader>

        {step === 'form' && (
          <div className="space-y-6 py-6">
            <OrderItemsList
              displayItems={displayItems}
              currentDesigns={currentDesigns}
              hasCurrentDesign={hasCurrentDesign}
              orderTotals={orderTotals}
              totalPrice={totalPrice}
              onEditItem={handleEditItem}
              onRemoveItem={removeFromCart}
              onUpdateQuantity={updateCartItemQuantity}
            />

            <CustomerDetailsForm
              formData={formData}
              onChange={handleInputChange}
              legalConfirmed={legalConfirmed}
              setLegalConfirmed={setLegalConfirmed}
              error={error}
            />
          </div>
        )}

        {step === 'processing' && <ProcessingStep />}
        {step === 'success' && <SuccessStep orderId={orderId} />}

        <SheetFooter className="mt-6">
          {step === 'form' && (
            <Button
              data-testid="checkout-submit-btn"
              onClick={handleCheckout}
              disabled={displayItems.length === 0}
              className="w-full h-14 rounded-full bg-gradient-to-r from-primary to-indigo-600 hover:from-primary/90 hover:to-indigo-600/90 text-white font-bold text-lg shadow-lg shadow-primary/25 transition-all duration-300 hover:shadow-xl disabled:opacity-50"
            >
              <ShoppingBag className="w-5 h-5 mr-2" />
              Pay ${totalPrice.toFixed(2)}
            </Button>
          )}

          {step === 'success' && (
            <Button
              data-testid="checkout-close-btn"
              onClick={handleClose}
              className="w-full h-14 rounded-full"
            >
              Create Another Design
            </Button>
          )}
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

export default CheckoutSheet;
